import React, { useState } from "react";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../ui/table";
import { Input } from "../../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Skeleton } from "../../ui/skeleton";
import { Separator } from "../../ui/separator";
import { 
  InfoIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  TestTubeIcon,
  CheckIcon,
  XIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";

interface RegexReplacement {
  id: string;
  pattern: string;
  replacement: string;
  matchCount?: number;
  example?: string;
  exampleReplaced?: string;
}

interface DatasetModificationProps {
  datasetId: number;
  isVisible: boolean;
  onClose: () => void;
}

const DatasetModification: React.FC<DatasetModificationProps> = ({
  datasetId,
  isVisible,
  onClose,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [regexReplacements, setRegexReplacements] = useState<RegexReplacement[]>([]);
  const [editingKey, setEditingKey] = useState<string>("");
  const [testingPattern, setTestingPattern] = useState<string>("");
  const [editPattern, setEditPattern] = useState<string>("");
  const [editReplacement, setEditReplacement] = useState<string>("");
  const [patternError, setPatternError] = useState<string>("");
  const [replacementError, setReplacementError] = useState<string>("");

  const isEditing = (record: RegexReplacement) => record.id === editingKey;

  const handleCancel = () => {
    setIsLoading(false);
    console.log(`Canceling dataset modification for dataset ${datasetId}`);
    onClose();
  };

  const handleAddRegexReplacement = () => {
    const newId = `regex-${Date.now()}`;
    const newReplacement: RegexReplacement = {
      id: newId,
      pattern: "",
      replacement: "",
    };
    setRegexReplacements([...regexReplacements, newReplacement]);
    setEditingKey(newId);
    setEditPattern("");
    setEditReplacement("");
    setPatternError("");
    setReplacementError("");
  };

  const handleDeleteRegexReplacement = (id: string) => {
    setRegexReplacements(regexReplacements.filter(item => item.id !== id));
  };

  const handleEditRegexReplacement = (record: RegexReplacement) => {
    setEditPattern(record.pattern);
    setEditReplacement(record.replacement);
    setEditingKey(record.id);
    setPatternError("");
    setReplacementError("");
  };

  const validateFields = () => {
    let isValid = true;
    
    if (!editPattern.trim()) {
      setPatternError("Please input a regex pattern");
      isValid = false;
    } else {
      setPatternError("");
    }
    
    if (!editReplacement.trim()) {
      setReplacementError("Please input a replacement");
      isValid = false;
    } else {
      setReplacementError("");
    }
    
    return isValid;
  };

  const handleSaveRegexReplacement = async (id: string) => {
    if (!validateFields()) {
      return;
    }

    const newData = [...regexReplacements];
    const index = newData.findIndex(item => id === item.id);
    
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { 
        ...item, 
        pattern: editPattern, 
        replacement: editReplacement 
      });
      setRegexReplacements(newData);
      setEditingKey("");
      
      // Test the pattern after saving
      testRegexPattern(editPattern, editReplacement, id);
    }
  };

  const handleCancelEdit = () => {
    // If it's a new empty row and user cancels, remove it
    if (editingKey && regexReplacements.find(r => r.id === editingKey)?.pattern === "") {
      setRegexReplacements(regexReplacements.filter(item => item.id !== editingKey));
    }
    setEditingKey("");
    setEditPattern("");
    setEditReplacement("");
    setPatternError("");
    setReplacementError("");
  };

  const testRegexPattern = async (pattern: string, replacement: string, id?: string) => {
    try {
      setTestingPattern(id || "all");
      DatasetService.getRegexReplacementsApplicableCount(
        datasetId,
        pattern,
        replacement,
        () => {},
        apiKey
      ).subscribe(
        (response) => {
          // Make sure we're handling the response correctly
          const matchCount = response.applicable_request_count;
          const example = response.example;
          const exampleReplaced = response.example_replaced;
          
          if (id) {
            // Update specific replacement with the count and examples
            setRegexReplacements(prev => 
              prev.map(item => 
                item.id === id 
                  ? { ...item, matchCount: matchCount, example: example, exampleReplaced: exampleReplaced } 
                  : item
              )
            );
          }
          
          setTestingPattern("");
        },
        (error) => {
          console.error("Error testing regex pattern:", error);
          setTestingPattern("");
        }
      );
    } catch (error) {
      console.error("Error testing regex pattern:", error);
      setTestingPattern("");
    }
  };

  const handleSubmit = () => {
    if (regexReplacements.length === 0) {
      // Show error state instead of notification
      return;
    }

    // Check if any regex replacement is being edited
    if (editingKey !== "") {
      // Show error state instead of notification
      return;
    }

    setIsLoading(true);

    // Format the regex replacements for the API
    const formattedReplacements = regexReplacements.map(({ pattern, replacement }) => ({
      pattern,
      replacement,
    }));

    DatasetService.applyRegexReplacements(
      datasetId,
      formattedReplacements,
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        setIsLoading(false);
        onClose();
      },
      (error) => {
        console.error("Error applying regex replacements:", error);
        setIsLoading(false);
      }
    );
  };

  const renderMatchCount = (record: RegexReplacement) => {
    const isLoading = testingPattern === record.id;
    
    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-16" />
          <span className="text-xs text-muted-foreground">Testing...</span>
        </div>
      );
    }
    
    if (record.matchCount !== undefined) {
      const count = typeof record.matchCount === "object" ? 0 : record.matchCount;
      
      return (
        <div className="space-y-1">
          <div className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            count > 0 
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400"
          )}>
            {count} {count === 1 ? "match" : "matches"}
          </div>
          {record.example && record.exampleReplaced && (
            <div className="text-xs text-muted-foreground max-w-[200px]">
              <div className="truncate">
                <span className="font-medium">Before:</span> {record.example}
              </div>
              <div className="truncate">
                <span className="font-medium">After:</span> {record.exampleReplaced}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <Button 
        variant="ghost"
        size="sm"
        onClick={() => testRegexPattern(record.pattern, record.replacement, record.id)}
        disabled={!record.pattern || editingKey !== "" || isLoading}
        className="gap-1"
      >
        <TestTubeIcon className="w-3 h-3" />
        Test
      </Button>
    );
  };

  const renderPatternCell = (record: RegexReplacement) => {
    const editable = isEditing(record);
    
    if (editable) {
      return (
        <div className="space-y-2">
          <Input
            value={editPattern}
            onChange={(e) => setEditPattern(e.target.value)}
            placeholder="e.g., .*:\\s*(yes|no).*"
            className={cn(
              "text-sm",
              patternError && "border-red-500 focus:border-red-500"
            )}
          />
          {patternError && (
            <p className="text-xs text-red-500">{patternError}</p>
          )}
        </div>
      );
    }
    
    return (
      <div className="text-sm font-gc-reg text-foreground">
        {record.pattern || "-"}
      </div>
    );
  };

  const renderReplacementCell = (record: RegexReplacement) => {
    const editable = isEditing(record);
    
    if (editable) {
      return (
        <div className="space-y-2">
          <Input
            value={editReplacement}
            onChange={(e) => setEditReplacement(e.target.value)}
            placeholder="e.g., $1"
            className={cn(
              "text-sm",
              replacementError && "border-red-500 focus:border-red-500"
            )}
          />
          {replacementError && (
            <p className="text-xs text-red-500">{replacementError}</p>
          )}
        </div>
      );
    }
    
    return (
      <div className="text-sm font-gc-reg text-foreground">
        {record.replacement || "-"}
      </div>
    );
  };

  const renderActions = (record: RegexReplacement) => {
    const editable = isEditing(record);
    
    if (editable) {
      return (
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost"
            size="sm"
            onClick={() => handleSaveRegexReplacement(record.id)} 
            disabled={isLoading}
            className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            <CheckIcon className="w-3 h-3" />
          </Button>
          <Button 
            variant="ghost"
            size="sm"
            onClick={handleCancelEdit} 
            disabled={isLoading}
            className="gap-1"
          >
            <XIcon className="w-3 h-3" />
          </Button>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-1">
        <Button 
          variant="ghost"
          size="sm"
          disabled={editingKey !== "" || isLoading} 
          onClick={() => handleEditRegexReplacement(record)}
          className="gap-1"
        >
          <EditIcon className="w-3 h-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDeleteRegexReplacement(record.id)}
          disabled={editingKey !== "" || isLoading}
          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <TrashIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={isVisible} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <EditIcon className="w-5 h-5 text-maitai-green" />
            Dataset Modification
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Information Card */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InfoIcon className="w-5 h-5 text-maitai-green" />
                Regex Replacements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground font-gc-reg">
                Regex replacements are applied to all responses in the dataset that match the pattern. 
                For example, if you want to convert "foo: bar" to just "bar", you can use a pattern 
                like ".*:\\s*(bar).*" with replacement "$1". The replacement can use capture groups from the pattern.
              </p>
              
              <div className="bg-card/50 border border-border rounded-lg p-4">
                <h4 className="text-sm font-gc-med text-foreground mb-2">Pattern Examples:</h4>
                <div className="space-y-2 text-xs text-muted-foreground font-mono">
                  <div><span className="font-medium">Pattern:</span> .*:\\s*(yes|no).* <span className="font-medium">Replacement:</span> $1</div>
                  <div><span className="font-medium">Result:</span> "Answer: yes, that's correct" → "yes"</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Regex Rules Table */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-gc-med text-foreground">
                  Replacement Rules
                </CardTitle>
                <Button 
                  onClick={handleAddRegexReplacement}
                  disabled={editingKey !== "" || isLoading}
                  className="gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {regexReplacements.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="space-y-3">
                    <h3 className="text-lg font-gc-med text-foreground">
                      No replacement rules
                    </h3>
                    <p className="text-muted-foreground max-w-md">
                      Add regex replacement rules to modify your dataset content.
                    </p>
                  </div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border hover:bg-transparent">
                      <TableHead className="w-[30%]">Pattern (Regex)</TableHead>
                      <TableHead className="w-[25%]">Replacement</TableHead>
                      <TableHead className="w-[25%]">Matches</TableHead>
                      <TableHead className="w-[20%]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {regexReplacements.map((record) => (
                      <TableRow
                        key={record.id}
                        className="hover:bg-accent/20 transition-colors border-b border-border/50"
                      >
                        <TableCell className="py-4">
                          {renderPatternCell(record)}
                        </TableCell>
                        <TableCell className="py-4">
                          {renderReplacementCell(record)}
                        </TableCell>
                        <TableCell className="py-4">
                          {renderMatchCount(record)}
                        </TableCell>
                        <TableCell className="py-4">
                          {renderActions(record)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Error States */}
          {regexReplacements.length === 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                Please add at least one regex replacement rule before applying modifications.
              </p>
            </div>
          )}

          {editingKey !== "" && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                Please save or cancel your current edits before applying modifications.
              </p>
            </div>
          )}

          <Separator className="border-border" />

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {regexReplacements.length} replacement {regexReplacements.length === 1 ? "rule" : "rules"} defined
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={handleCancel} 
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isLoading || regexReplacements.length === 0 || editingKey !== ""}
                className="gap-2"
              >
                {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                Apply Modifications
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DatasetModification; 